// store.jsx — CardFloatingActionsContext (reglas + acciones de flujo)
import { createContext } from "preact";
import { useContext, useMemo, useState, useCallback } from "preact/hooks";
import { fetchToServer } from "@shared/components";
import { useQuotationByIdMultiRole } from "../store"; // 👈 MISMO contexto padre
import { navigate } from "../../../utils/Navigator";

export const CardFloatingActionsContext = createContext(null);

export function useCardFloatingActions() {
  const ctx = useContext(CardFloatingActionsContext);
  if (!ctx) throw new Error("useCardFloatingActions must be used within CardFloatingActionsProvider");
  return ctx;
}

export function CardFloatingActionsProvider({ children }) {
  const { quotation, quotationId, role, refresh } = useQuotationByIdMultiRole();

  const reqs = Array.isArray(quotation?.requirements) ? quotation.requirements : [];
  const statusName = String(quotation?.status?.name || "").toLowerCase();
  const allReqsConfirmed =
   reqs.length > 0 &&
   reqs.every(r => String(r?.status?.name || "").toLowerCase() === "confirmed");
  const isClient = role === "adevs-client";
  const isAdmin  = role === "adevs-admin";
  const showClientContinue = isClient && statusName === "created" && allReqsConfirmed;
  const showClientAccept = isClient && statusName === "quoted";
  const showAdminReject    = isAdmin  && ["created","reviewing","ai-review","quoted"].includes(statusName);
  const showClientReject   = isClient && ["quoted"].includes(statusName);
  const showAdminGenerate  = isAdmin  && ["reviewing"].includes(statusName);

  // Modales
  const [openReject, setOpenReject] = useState(false);
  const [openResult, setOpenResult] = useState(false);

  // Loading flags por acción
  const [continuing, setContinuing] = useState(false);
  const [rejecting, setRejecting]   = useState(false);
  const [generating, setGenerating] = useState(false);

  // Acciones (usa tus endpoints reales)
  const clientContinue = useCallback(async () => {
    if (!quotationId) return;
    setContinuing(true);
    try {
      await fetchToServer({
        url: `/${role}/api/quotations/${quotationId}/status/ai-review`,
        method: "get",
        withCredentials: true,
      });
      await refresh();
    } finally {
      setContinuing(false);
    }
  }, [role, quotationId, refresh]);

  const submitReject = useCallback(async ({ rejectedRole, reasonCode, description }) => {
    if (!quotationId) return;
    setRejecting(true);
    try {
      await fetchToServer({
        url: `/${role}/api/quotations/${quotationId}/reject`,
        method: "post",
        data: { rejectedRole, reasonCode, description },
        withCredentials: true,
      });
      setOpenReject(false);
      await refresh();
    } finally {
      setRejecting(false);
    }
  }, [role, quotationId, refresh]);

  const submitResult = useCallback(async (payload) => {
    if (!quotationId) return;
    setGenerating(true);
    try {
      await fetchToServer({
        url: `/${role}/api/quotations/${quotationId}/results/create`,
        method: "post",
        data: payload,
        withCredentials: true,
      });
      setOpenResult(false);
      await refresh();
    } finally {
      setGenerating(false);
    }
  }, [role, quotationId, refresh]);

  const acceptAction = ()=> navigate(`/${role}/quotations/${quotationId}/accept`);
  const value = useMemo(
    () => ({
      // contexto padre
      quotation, quotationId, role, statusName,
      // visibilidad
      showClientContinue, showAdminReject, showClientReject, showAdminGenerate, showClientAccept,
      // modales
      openReject, setOpenReject,
      openResult, setOpenResult,
      // acciones
      clientContinue, submitReject, submitResult, acceptAction,
      // loading
      continuing, rejecting, generating
      
    }),
    [
      quotation, quotationId, role, statusName,
      showClientContinue, showAdminReject, showClientReject, showAdminGenerate, showClientAccept,
      openReject, openResult,
      clientContinue, submitReject, submitResult,
      continuing, rejecting, generating
    ]
  );

  return (
    <CardFloatingActionsContext.Provider value={value}>
      {children}
    </CardFloatingActionsContext.Provider>
  );
}
